import { FC, useCallback, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// MUI
import { Tooltip } from '@material-ui/core';
// Components
import { Input } from 'components/Controls';
import { Button, IconButton } from 'components/Buttons';
// Icons
import { FaTrash } from 'react-icons/fa';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';

type Props = {
  name: string;
  index: number;
  onDelete: () => void;
  error?: any;
}

const TopListItem:FC<Props> = ({ name, index, onDelete, error }) => {
  const { t } = useTranslation();

  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `${name}.${index}.correctAnswers`
  });

  const onAdd = useCallback(() => {
    append({ title: '' });
  }, [append]);

  const onRemove = (index:number) => () => remove(index);

  useEffect(() => {
    if (fields.length === 0) {
      onAdd();
    }
  }, [fields, onAdd]);

  return (
    <div className="flex-grow">
      <div className="flex gap-4 mb-1">
        <div className="flex-grow">
          <p className="block text-white text-sm font-semibold mb-1">
            {`${t('labels.item')} ${index + 1}`}
          </p>
          {fields.map((field, i) => (
            <div key={field.id} className="flex gap-2">
              <Controller
                control={control}
                rules={{ required: isRequired, validate: noWhitespace }}
                name={`${name}.${index}.correctAnswers.${i}.title`}
                render={({ field }) => (
                  <Input
                    {...field}
                    className="mb-2 flex-grow"
                    id={`${name}.${index}`}
                    required
                    error={!!error?.correctAnswers?.[0]?.title}
                    errorText={error?.correctAnswers?.[0]?.title?.message}
                  />
                )}
              />
              {i > 0 ? (
                <Tooltip title={t('buttons.remove')}>
                  <div>
                    <IconButton
                      onClick={onRemove(i)}
                    ><FaTrash /></IconButton>
                  </div>
                </Tooltip>
              ) : null}
            </div>
          ))}
          <Button onClick={onAdd}>Add alternative answer</Button>
        </div>
        <Controller
          control={control}
          name={`${name}.${index}.scoring.points`}
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Input
              {...field}
              id={`${name}.${index}.scoring.points`}
              label={t('labels.points')}
              type="number"
              required
              error={Boolean(error?.scoring?.points)}
              errorText={error?.scoring?.points?.message}
            />
          )}
        />
        <div className="mt-5">
          <Tooltip title={t('buttons.remove')}>
            <div>
              <IconButton
                onClick={onDelete}
              ><FaTrash /></IconButton>
            </div>
          </Tooltip>
        </div>
      </div>
      <span className="divider"></span>
    </div>
  )
}

export default TopListItem;
